import { translate } from 'components/Translation';
import React, { useMemo } from 'react';
import _ from 'lodash';
import { BodyM, BodyS, Caption } from 'components/Typography/Typography';
import Phone from '@material-ui/icons/PhoneOutlined';
import Badge from 'components/Badge/Badge';
import classNames from 'classnames';
import styles from 'assets/jss/material-dashboard-pro-react/components/addNumberDialogStyle';
import { makeStyles } from '@material-ui/core';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import Account from '@material-ui/icons/AccountCircleOutlined';
import ArrowForward from '@material-ui/icons/ArrowForward';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IConfirmationDialogProps {
	filteredNumbers: Array<SingleNumber>;
	selectedNumbers: Array<types.organizations.INumber>;
	departmentsToChange: Array<types.organizations.INumber>;
	confirmationDialogState: 'departments' | 'numbers';
	cancelSkipClicked: () => void;
	changeAddClicked: () => void;
	isOpen: boolean;
	hideDialog: () => void;
}

const AddNumbersConfirmationDialog = ({
	selectedNumbers,
	filteredNumbers,
	isOpen,
	departmentsToChange,
	confirmationDialogState,
	cancelSkipClicked,
	changeAddClicked,
	hideDialog,
}: IConfirmationDialogProps) => {
	const classes = useStyles();

	const extendedNumbers = useMemo(() => {
		const accountNums = _.map(selectedNumbers, 'accountNum');
		return _.chain(filteredNumbers)
			.map((number) => {
				return {
					...number,
					added: accountNums.indexOf(number.accountNum) >= 0,
				};
			})
			.groupBy('userId')
			.toPairs()
			.value();
	}, [filteredNumbers, selectedNumbers]);

	return (
		<ConfirmationDialog
			isOpen={isOpen}
			className={classes.confirmationDialog}
			hideDialog={hideDialog}
			header={
				confirmationDialogState === 'departments'
					? translate('number.details.change.department')
					: translate('departments.missing.numbers')
			}
			dialogBody={
				<div className={classes.confirmationDialogBody}>
					<ConditionalRender
						show={confirmationDialogState === 'departments'}
						onTrue={() => (
							<>
								<BodyS color="primary-light">
									{translate('departments.numbers.users.can.be.assigned')}
								</BodyS>
								<BodyS color="primary-light">{translate('departments.do.you.want.to.change')}</BodyS>
								{departmentsToChange.map((number: types.organizations.INumber) => (
									<div className={classes.changingDepartmentInfo} key={number.accountNum}>
										<Phone className={classes.phoneIcon} />
										<Caption color="secondary-light" disableMargin>
											{number.accountNum}
										</Caption>
										{number.username && (
											<>
												<Account className={classes.phoneIcon} />
												<Caption color="secondary-light" disableMargin>
													{number.username}
												</Caption>
											</>
										)}
										<ConditionalRender
											show={!!number.department}
											onTrue={() => (
												<Badge filled hexColor={number.department!.uiColor}>
													{number.department!.name}
												</Badge>
											)}
											onFalse={() => (
												<BodyM color="secondary-light" disableMargin>
													{translate('numbers.details.notAssigned')}
												</BodyM>
											)}
										/>
										<ArrowForward className={classes.arrowForwardIcon} />
										<ConditionalRender
											show={!!number.selectedDepartment}
											onTrue={() => (
												<Badge filled hexColor={number.selectedDepartment!.uiColor}>
													{number.selectedDepartment!.name}
												</Badge>
											)}
										/>
									</div>
								))}
							</>
						)}
					/>
					<ConditionalRender
						show={confirmationDialogState === 'numbers'}
						onTrue={() => (
							<>
								<BodyS color="primary-light">{translate('departments.do.you.want.to.add.them')}</BodyS>
								<div className={classes.additionalMargin}>
									<BodyS color="primary-light">{translate('departments.if.you.will.add')}</BodyS>
								</div>
								{extendedNumbers.map((numbersGroup) => (
									<div key={numbersGroup[0][0]} className={classes.numbersGroup}>
										<div>
											<Account />
											<Caption disableMargin color="primary-light">
												{numbersGroup[1][0].username}
											</Caption>
										</div>
										<div>
											{numbersGroup[1].map((number) => (
												<div key={number.accountNum}>
													<Phone />
													<div
														className={classNames({
															[classes.notAdded]: !number.added,
														})}
													>
														<Caption
															color="primary-light"
															key={number.accountNum}
															disableMargin
														>
															<>
																{number.accountNum}
																{!number.added && ` (${translate('not.added')})`}
															</>
														</Caption>
													</div>
												</div>
											))}
										</div>
									</div>
								))}
							</>
						)}
					/>
				</div>
			}
			buttons={[
				{
					text: confirmationDialogState === 'departments' ? translate('cancel') : translate('skip'),
					onClick: cancelSkipClicked,
					color: 'tertiary-light',
				},
				{
					text: confirmationDialogState === 'departments' ? translate('change') : translate('add'),
					onClick: changeAddClicked,
					color: 'primary-light',
				},
			]}
		/>
	);
};

export default AddNumbersConfirmationDialog;
